import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { relatedGuides } from "@/lib/related-guides";
import { GUIDE_REFS, GUIDE_TOTAL } from "@/lib/guides-index";
import type { Guide } from "@/lib/guides-data";
import { ShareButton } from "@/components/ShareButton";
import { clipTitle, pageTitle } from "@/lib/seo-title";
import { snippetFor } from "@/lib/seo-snippets";
import { PageExpansion } from "@/components/PageExpansion";
import { MoneyPageLinks } from "@/components/MoneyPageLinks";
import { ContextVisualStrip, guideVisualVariant } from "@/components/ContextVisualStrip";
import ss01KeyVisual from "@/assets/ss01/slide.webp.asset.json";

export const Route = createFileRoute("/guides/$slug/")({
  // ?from=map marks a visitor who arrived from the interactive map, so the page
  // can offer a direct route back to the map they were exploring.
  validateSearch: (search: Record<string, unknown>): { from?: "map" } =>
    search.from === "map" ? { from: "map" } : {},
  // The guide corpus is ~700 KB of prose. It is imported dynamically here so it
  // lands in this route's own chunk instead of the sitewide entry bundle
  // (Reduce unused JavaScript); the light slug/title index above covers the
  // sibling links that render on every guide page.
  loader: async ({ params }): Promise<{ guide: Guide }> => {
    const { GUIDE_BY_SLUG } = await import("@/lib/guides-data");
    const guide = GUIDE_BY_SLUG[params.slug];
    if (!guide) {
      // Retired fan-variant guide slugs get one 301 to their base guide.
      const { resolveRetiredSlug, retiredSectionIndex } = await import("@/lib/seo-redirects");
      const base = resolveRetiredSlug(params.slug, (s) => !!GUIDE_BY_SLUG[s]);
      throw redirect({ href: base ? `/guides/${base}` : retiredSectionIndex("guides"), statusCode: 301 });
    }
    return { guide };
  },

  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Guide not found — Pal Stars" }, { name: "robots", content: "noindex" }] };
    const g = loaderData.guide;
    const clip = clipTitle;
    // Guides that already rank get a hand-written, click-worthy snippet; every
    // other guide keeps the generated long-tail title and description.
    const override = snippetFor(`/guides/${g.slug}`);
    const title = override?.title ?? pageTitle(g.title);
    const desc = override?.description ?? clip(g.desc, 155);
    const isSs01 = g.slug === "palworld-tcg-sleeve-card-set-vol-1";
    const image = isSs01 ? `https://palstars.com${ss01KeyVisual.url}` : null;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: override?.social?.title ?? title },
        { property: "og:description", content: override?.social?.description ?? desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://palstars.com/guides/${g.slug}` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: override?.social?.title ?? title },
        { name: "twitter:description", content: override?.social?.description ?? desc },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
      ],


      links: [{ rel: "canonical", href: `https://palstars.com/guides/${g.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.title,
            description: g.desc,
              ...(image ? { image } : {}),
            keywords: g.keywords,
            author: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/", logo: "https://palstars.com/eidrolon-avatar.png" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/", logo: "https://palstars.com/eidrolon-avatar.png" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".aeo-answer"] },
            mainEntity: (() => {
              const seen = new Set<string>();
              const out: Array<{ "@type": string; name: string; acceptedAnswer: { "@type": string; text: string } }> = [];
              for (const a of g.articles) {
                const name = a.title.endsWith("?") ? a.title : `${a.title}?`;
                if (seen.has(name) || !a.intro) continue;
                seen.add(name);
                out.push({ "@type": "Question", name, acceptedAnswer: { "@type": "Answer", text: a.intro } });
              }
              return out;
            })(),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${g.title} — articles`,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            numberOfItems: g.articles.length,
            itemListElement: g.articles.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: a.title,
              url: `https://palstars.com/guides/${encodeURIComponent(g.slug)}/${encodeURIComponent(a.slug)}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
              { "@type": "ListItem", position: 2, name: "Guides", item: { "@id": "https://palstars.com/guides", name: "Guides" } },
              { "@type": "ListItem", position: 3, name: g.title, item: { "@id": `https://palstars.com/guides/${encodeURIComponent(g.slug)}`, name: g.title } },
            ],
          }),
        },
      ],
    };
  },
  component: GuideDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That guide doesn't exist"
      message="No Palworld guide matches this URL. Browse all guides to find breeding, farming, mount and TCG strategy walkthroughs."
    />
  ),
});

function GuideDetail() {
  const { guide } = Route.useLoaderData() as { guide: Guide };
  const { from } = Route.useSearch();
  const idx = GUIDE_REFS.findIndex((x) => x.slug === guide.slug);
  const prev = GUIDE_REFS[(idx - 1 + GUIDE_REFS.length) % GUIDE_REFS.length];
  const next = GUIDE_REFS[(idx + 1) % GUIDE_REFS.length];
  const related = relatedGuides(guide, 4);


  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <div className="mb-4 flex flex-wrap gap-2">
        {from === "map" && (
          <Link to="/map" className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20">
            ← Back to the interactive map
          </Link>
        )}
        <Link to="/guides" className="inline-flex items-center gap-1 rounded-full border bg-card/60 px-3 py-1.5 text-xs hover:bg-card">← Back to all guides</Link>
      </div>
      <nav className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <Link to="/guides" className="hover:text-foreground">Guides</Link> /{" "}
        <span className="text-foreground">{guide.title}</span>
      </nav>

      <span className="text-[10px] uppercase tracking-widest text-primary">{guide.tag}</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">{guide.title}</h1>
      <p className="mt-2 text-muted-foreground">{guide.desc}</p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">Updated for the current Palworld patch</p>
        <ShareButton title={guide.title} text={guide.desc} path={`/guides/${guide.slug}`} />
      </div>
      {guide.slug === "palworld-tcg-sleeve-card-set-vol-1" ? (
        <figure className="mt-6 overflow-hidden rounded-lg border border-border/60 bg-background/40">
          <img src={ss01KeyVisual.url} alt="Palworld OFFICIAL CARD GAME Sleeve & Card Set Vol.1 official product visual" width={1200} height={675} loading="lazy" decoding="async" className="aspect-video w-full object-contain" />
        </figure>
      ) : (
        <ContextVisualStrip variant={guideVisualVariant(`${guide.title} ${guide.desc} ${guide.tag}`)} />
      )}

      <aside className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-primary mb-2">Quick answer</div>
        <p className="aeo-answer text-sm leading-relaxed">{guide.desc}</p>
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2 text-sm">
          {guide.articles.slice(0, 6).map((a) => (
            <li key={a.slug} className="flex gap-2">
              <span className="text-primary">•</span>
              <Link to="/guides/$slug/$article" params={{ slug: guide.slug, article: a.slug }} className="hover:text-foreground">{a.title}</Link>
            </li>
          ))}
        </ul>
      </aside>

      <div className="mt-6 rounded-xl border bg-card/40 p-4">
        <h2 className="text-sm font-semibold">Articles in this guide</h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {guide.articles.map((article) => (
            <Link key={article.slug} to="/guides/$slug/$article" params={{ slug: guide.slug, article: article.slug }} className="rounded-lg border bg-background/40 p-3 text-sm hover:bg-card">
              <span className="font-medium">{article.title}</span>
              <span className="mt-1 block text-xs text-primary">Read article →</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {guide.articles.map((article, ai) => (
          <Link
            key={article.slug}
            to="/guides/$slug/$article"
            params={{ slug: guide.slug, article: article.slug }}
            className="glass rounded-2xl p-6 card-hover block"
          >
            <div className="text-[10px] uppercase tracking-widest text-primary">Article {ai + 1}</div>
            <h2 className="mt-1 text-xl font-bold hover:text-primary transition-colors">{article.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{article.intro}</p>
            <div className="mt-3 rounded-lg border bg-background/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-primary mb-1">In this article</div>
              <ul className="grid gap-1 text-xs">
                {article.sections.slice(0, 3).map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-primary">✓</span><span>{s.heading}</span></li>
                ))}
              </ul>
            </div>
            <span className="mt-4 inline-block text-sm text-primary">Read full article →</span>
          </Link>
        ))}
      </div>

      <PageExpansion path={`/guides/${guide.slug}`} />

      <MoneyPageLinks topic={`${guide.title} ${guide.tag} ${guide.keywords ?? ""}`} />

      <div className="mt-12 rounded-2xl border bg-card/40 p-6">
        <h2 className="text-lg font-semibold">Continue exploring Pal Stars</h2>
        <p className="mt-1 text-sm text-muted-foreground">Related guides, calculators and databases updated for the current Palworld patch.</p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {related.map((g) => (
            <Link key={g.slug} to="/guides/$slug" params={{ slug: g.slug }} className="rounded-lg border bg-background/40 p-3 text-sm hover:bg-card">
              <span className="font-medium">{g.title}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{g.tag}</span>
            </Link>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <Link to="/pals" className="rounded-full border px-3 py-1.5 hover:bg-card">All Pals</Link>
          <Link to="/breeding" className="rounded-full border px-3 py-1.5 hover:bg-card">Breeding Calculator</Link>
          <Link to="/mounts" className="rounded-full border px-3 py-1.5 hover:bg-card">Mount Speeds</Link>
          <Link to="/capture-rates" className="rounded-full border px-3 py-1.5 hover:bg-card">Capture Rates</Link>
          <Link to="/elements" className="rounded-full border px-3 py-1.5 hover:bg-card">Elemental Chart</Link>
          <Link to="/cards" className="rounded-full border px-3 py-1.5 hover:bg-card">TCG Cards</Link>
          <Link to="/deck-builder" className="rounded-full border px-3 py-1.5 hover:bg-card">Deck Builder</Link>
          <Link to="/eidrolon" className="rounded-full border px-3 py-1.5 hover:bg-card">Eidrolon Wiki</Link>
          <Link to="/map" className="rounded-full border px-3 py-1.5 hover:bg-card">Interactive Map</Link>
          <Link to="/bosses" className="rounded-full border px-3 py-1.5 hover:bg-card">Boss Database</Link>
          <Link to="/guides" className="rounded-full border px-3 py-1.5 hover:bg-card">All {GUIDE_TOTAL} guides</Link>
        </div>
      </div>

      <div className="mt-10 flex justify-between text-sm">
        <Link to="/guides/$slug" params={{ slug: prev.slug }} className="text-primary hover:underline">← {prev.title}</Link>
        <Link to="/guides/$slug" params={{ slug: next.slug }} className="text-primary hover:underline">{next.title} →</Link>
      </div>
    </article>
  );
}